import React, { useEffect, useState } from 'react';
import { orderService } from '@/application/services';
import {
  Order,
  OrderStatus,
  OrderDetail,
  OrderFilters,
} from '@/domain/entities';
import { Pagination } from '../components/Pagination';
import { OrdersTable } from '../components/OrdersTable';
import { OrdersFilters as OrdersFiltersComponent } from '../components/OrdersFilters';
import { CreateOrderForm } from '../components/CreateOrderForm';
import { HttpError, CreateOrderDto } from '@/domain/entities';

const STATUSES: { value: OrderStatus; label: string }[] = [
  { value: 'pending', label: 'Ожидает' },
  { value: 'confirmed', label: 'Подтвержден' },
  { value: 'shipped', label: 'Отгружен' },
  { value: 'delivered', label: 'Доставлен' },
  { value: 'cancelled', label: 'Отменён' },
];

const DEFAULT_LIMIT = 20;

export const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<OrderFilters>({ limit: DEFAULT_LIMIT, offset: 0 });

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [statusUpdateNote, setStatusUpdateNote] = useState('');
  const [statusUpdateValue, setStatusUpdateValue] = useState<OrderStatus>('pending');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentUpdating, setCommentUpdating] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.limit, filters.offset, filters.status, filters.search, filters.dateFrom, filters.dateTo, filters.userId]);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await orderService.getOrders(filters);
      setOrders(response.orders);
      setTotal(response.total);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Не удалось загрузить заказы');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters: OrderFilters) => {
    setFilters(newFilters);
  };

  const handlePageChange = (newOffset: number) => {
    setFilters((prev) => ({ ...prev, offset: newOffset }));
  };

  const handleOpenOrder = async (order: Order) => {
    setSelectedOrderId(order.id);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await orderService.getOrder(order.id);
      setOrderDetail(response.order);
      setStatusUpdateValue(response.order.status);
      setCommentDraft(response.order.comment || '');
    } catch (err) {
      if (err instanceof HttpError) {
        setDetailError(err.data?.error || err.message);
      } else if (err instanceof Error) {
        setDetailError(err.message);
      } else {
        setDetailError('Не удалось загрузить заказ');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseOrder = () => {
    setSelectedOrderId(null);
    setOrderDetail(null);
    setStatusUpdateNote('');
    setCommentDraft('');
    setDetailError(null);
    setDeleteLoading(false);
  };

  const handleStatusUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderDetail) return;
    setStatusUpdating(true);
    try {
      const response = await orderService.updateStatus(orderDetail.id, {
        status: statusUpdateValue,
        note: statusUpdateNote.trim() || undefined,
      });
      setOrderDetail(response.order);
      setStatusUpdateNote('');
    } catch (err) {
      if (err instanceof HttpError) {
        setDetailError(err.data?.error || err.message);
      } else if (err instanceof Error) {
        setDetailError(err.message);
      } else {
        setDetailError('Не удалось обновить статус заказа');
      }
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleCommentUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderDetail) return;
    setCommentUpdating(true);
    try {
      const response = await orderService.updateComment(orderDetail.id, {
        comment: commentDraft,
      });
      setOrderDetail(response.order);
    } catch (err) {
      if (err instanceof HttpError) {
        setDetailError(err.data?.error || err.message);
      } else if (err instanceof Error) {
        setDetailError(err.message);
      } else {
        setDetailError('Не удалось обновить комментарий');
      }
    } finally {
      setCommentUpdating(false);
    }
  };

  const handleCreateOrder = async (orderData: CreateOrderDto) => {
    try {
      await orderService.createOrder(orderData);
      setShowCreateForm(false);
      await loadOrders(); // Перезагружаем список заказов
    } catch (err) {
      if (err instanceof HttpError) {
        throw new Error(err.data?.error || err.message);
      } else if (err instanceof Error) {
        throw err;
      } else {
        throw new Error('Не удалось создать заказ');
      }
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderDetail) return;
    if (!window.confirm('Удалить заказ без возможности восстановления?')) {
      return;
    }
    setDeleteLoading(true);
    try {
      await orderService.deleteOrder(orderDetail.id);
      handleCloseOrder();
      await loadOrders();
    } catch (err) {
      if (err instanceof HttpError) {
        setDetailError(err.data?.error || err.message);
      } else if (err instanceof Error) {
        setDetailError(err.message);
      } else {
        setDetailError('Не удалось удалить заказ');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex-shrink-0 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Заказы</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Контроль заказов и управление статусами • Всего: {total}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateForm(true)}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
        >
          Добавить заказ
        </button>
      </div>

      {/* Divider */}
      <div className="flex-shrink-0 mt-4 border-t border-gray-200 dark:border-gray-700"></div>

      {/* Filters */}
      <div className="flex-shrink-0 mt-6">
        <OrdersFiltersComponent
          filters={filters}
          onChange={handleFiltersChange}
          onReset={() => setFilters({ limit: DEFAULT_LIMIT, offset: 0 })}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex-shrink-0 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-2 rounded">
          {error}
        </div>
      )}

      {/* Table with Pagination */}
      <div className="flex-1 flex flex-col min-h-0 mt-4">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">Загрузка...</div>
        ) : (
          <>
            <div className="flex-1 overflow-auto min-h-0">
              <OrdersTable
                orders={orders}
                onViewDetails={handleOpenOrder}
              />
            </div>
            <div className="flex-shrink-0 mt-4">
              <Pagination
                total={total}
                limit={filters.limit || DEFAULT_LIMIT}
                offset={filters.offset || 0}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>

      {selectedOrderId && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4 py-8 bg-black/50" onClick={handleCloseOrder}>
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Заказ {orderDetail?.orderNumber || ''}</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDeleteOrder}
                  disabled={deleteLoading}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  {deleteLoading ? 'Удаление...' : 'Удалить'}
                </button>
                <button onClick={handleCloseOrder} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-64px)]">
              {detailLoading ? (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">Загрузка заказа...</div>
              ) : detailError ? (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
                  {detailError}
                </div>
              ) : orderDetail ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Покупатель</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">{orderDetail.customerName}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{orderDetail.customerContactValue}</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Доставка</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">{orderDetail.deliveryType}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{orderDetail.deliveryAddress || '—'}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-700 rounded-lg p-4">
                      <div className="text-sm text-indigo-700 dark:text-indigo-300">Статус</div>
                      <div className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
                        {STATUSES.find((s) => s.value === orderDetail.status)?.label || orderDetail.status}
                      </div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/30 border border-green-100 dark:border-green-700 rounded-lg p-4">
                      <div className="text-sm text-green-700 dark:text-green-300">Сумма</div>
                      <div className="text-lg font-semibold text-green-900 dark:text-green-100">{orderDetail.totalPrice.toLocaleString('ru-RU')} ₽</div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-100 dark:border-purple-700 rounded-lg p-4">
                      <div className="text-sm text-purple-700 dark:text-purple-300">Создан</div>
                      <div className="text-lg font-semibold text-purple-900 dark:text-purple-100">{new Date(orderDetail.createdAt).toLocaleString('ru-RU')}</div>
                    </div>
                  </div>

                  <form onSubmit={handleStatusUpdate} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3 bg-gray-50 dark:bg-gray-700/30">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Обновить статус</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Статус</label>
                        <select
                          value={statusUpdateValue}
                          onChange={(e) => setStatusUpdateValue(e.target.value as OrderStatus)}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          {STATUSES.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Комментарий</label>
                        <input
                          type="text"
                          value={statusUpdateNote}
                          onChange={(e) => setStatusUpdateNote(e.target.value)}
                          placeholder="Комментарий к изменению"
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={statusUpdating}
                        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        {statusUpdating ? 'Сохранение...' : 'Сохранить' }
                      </button>
                    </div>
                  </form>

                  <form onSubmit={handleCommentUpdate} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3 bg-gray-50 dark:bg-gray-700/30">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Комментарий менеджера</h3>
                    <textarea
                      value={commentDraft}
                      onChange={(e) => setCommentDraft(e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Заметки по заказу"
                    />
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={commentUpdating}
                        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        {commentUpdating ? 'Сохранение...' : 'Обновить комментарий'}
                      </button>
                    </div>
                  </form>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Состав заказа</h3>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {(orderDetail.items || []).map((item) => (
                        <div key={`${item.sku}-${item.size}`} className="p-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <img
                            src={item.imageUrl || 'https://via.placeholder.com/64x64?text=No+Image'}
                            alt={item.productName}
                            className="w-16 h-16 rounded object-cover border border-gray-200 dark:border-gray-600"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/64x64?text=No+Image';
                            }}
                          />
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">{item.productName}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">SKU: {item.sku}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">Размер: {item.size || '—'} · Кол-во: {item.quantity}</div>
                          </div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">{item.price.toLocaleString('ru-RU')} ₽</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">История действий</h3>
                    {(!orderDetail.actions || orderDetail.actions.length === 0) ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400">История отсутствует</div>
                    ) : (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                        {(orderDetail.actions || []).map((action) => (
                          <div key={action.id} className="p-4 flex flex-col md:flex-row md:justify-between md:items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <div>
                              <div className="text-sm font-semibold text-gray-900 dark:text-white">{action.adminEmail}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {new Date(action.createdAt).toLocaleString('ru-RU')}
                              </div>
                            </div>
                            <div className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                              <div className="font-medium text-gray-900 dark:text-white mb-1">{action.action}</div>
                              {action.note && <div className="text-gray-600 dark:text-gray-400">Комментарий: {action.note}</div>}
                              {(action.fromStatus || action.toStatus) && (
                                <div className="text-gray-600 dark:text-gray-400">
                                  Статус: {action.fromStatus || '—'} → {action.toStatus || '—'}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">Данные заказа отсутствуют</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Форма создания заказа */}
      {showCreateForm && (
        <CreateOrderForm
          onClose={() => setShowCreateForm(false)}
          onSubmit={handleCreateOrder}
        />
      )}
    </div>
  );
};

export default OrdersPage;

